import { useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { STATUSES, STATUS_LABELS, STATUS_COLORS } from "@/lib/constants";
import { updateIssue } from "@/lib/api";
import KanbanCard from "./KanbanCard";

export default function KanbanBoard({ issues, users, onSelectIssue, onIssueUpdated, onOptimisticStatusChange }) {
  const getIssuesByStatus = (status) =>
    issues.filter((i) => i.status === status);

  const handleDragEnd = useCallback(
    async (result) => {
      if (!result.destination) return;
      const { draggableId, destination } = result;
      const newStatus = destination.droppableId;
      const issue = issues.find((i) => i.id === draggableId);
      if (!issue || issue.status === newStatus) return;

      // Optimistic update: move card instantly in local state
      if (onOptimisticStatusChange) {
        onOptimisticStatusChange(draggableId, newStatus);
      }

      // Sync with backend silently
      try {
        await updateIssue(issue.issue_key, { status: newStatus });
      } catch (e) {
        console.error("Failed to update issue status", e);
        // Revert on failure
        if (onIssueUpdated) onIssueUpdated();
      }
    },
    [issues, onIssueUpdated, onOptimisticStatusChange]
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div
        className="flex-1 overflow-x-auto overflow-y-hidden p-6 bg-white h-full"
        data-testid="kanban-board"
      >
        <div className="flex h-full gap-5 items-start">
          {STATUSES.map((status) => {
            const statusIssues = getIssuesByStatus(status);
            const colors = STATUS_COLORS[status];
            return (
              <div
                key={status}
                className="flex flex-col min-w-[300px] w-[300px] max-h-full bg-[#F9FAFB] border border-gray-200 rounded-sm"
                data-testid={`kanban-column-${status}`}
              >
                {/* Column header */}
                <div className="flex items-center justify-between p-3 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: colors.text }}
                    />
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
                      {STATUS_LABELS[status]}
                    </span>
                  </div>
                  <span className="text-xs font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-sm">
                    {statusIssues.length}
                  </span>
                </div>

                {/* Column body */}
                <Droppable droppableId={status}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-1 overflow-y-auto p-2 space-y-2 min-h-[100px] transition-colors ${
                        snapshot.isDraggingOver ? "bg-gray-100" : ""
                      }`}
                    >
                      {statusIssues.map((issue, index) => (
                        <Draggable
                          key={issue.id}
                          draggableId={issue.id}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <KanbanCard
                                issue={issue}
                                users={users}
                                isDragging={snapshot.isDragging}
                                onClick={() => onSelectIssue(issue.issue_key)}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </div>
    </DragDropContext>
  );
}
